import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Input } from 'designSystem/components/Input'
import { Spinner } from 'designSystem/components/Spinner'
import { Text } from 'designSystem/components/Text'
import type { IntraDocumentSearchState } from 'features/knowledgeDocumentViewer/hooks/useIntraDocumentSearch'
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import React, { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { MOBILE_BREAKPOINT_PX } from 'utils/mobileLayout'

const SearchBar = styled.div`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  right: 0.5rem;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background-color: var(--muted);

  @media (min-width: ${MOBILE_BREAKPOINT_PX}px) {
    left: auto;
  }
`

const FindField = styled.div`
  flex: 1 1 auto;

  @media (min-width: ${MOBILE_BREAKPOINT_PX}px) {
    flex: 0 0 12.5rem;
  }
`

const MatchCountSlot = styled.span<{ $isLoading: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: ${({ $isLoading }) =>
    $isLoading ? 'flex-start' : 'center'};
  min-width: 3.75rem;
`

type IntraDocSearchBarProps = {
  searchState: IntraDocumentSearchState
}

export function IntraDocSearchBar({ searchState }: IntraDocSearchBarProps) {
  const {
    query,
    hits,
    activeHitIndex,
    isLoading,
    setQuery,
    goToNext,
    goToPrevious,
    close,
  } = searchState

  const { t } = useLingui()
  const inputRef = useRef<HTMLInputElement>(null)

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      } else if (
        (event.key === 'Enter' && event.shiftKey) ||
        event.key === 'ArrowUp'
      ) {
        event.preventDefault()
        goToPrevious()
      } else if (event.key === 'Enter' || event.key === 'ArrowDown') {
        event.preventDefault()
        goToNext()
      }
    },
    [close, goToNext, goToPrevious]
  )

  const activeHitNumber = activeHitIndex + 1
  const hitCount = hits.length
  const matchCountText =
    hitCount === 0
      ? query.trim().length > 0
        ? t`No results`
        : ''
      : t`${activeHitNumber} of ${hitCount}`

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <SearchBar role='search'>
      <FindField>
        <Input
          ref={inputRef}
          type='text'
          inputSize='toolbar'
          placeholder={t`Find in document...`}
          aria-label={t`Find in document`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </FindField>
      <MatchCountSlot $isLoading={isLoading} role='status'>
        {isLoading ? (
          <Spinner size={12} aria-label={t`Searching...`} />
        ) : (
          <Text variant='caption' weight='medium' color='default' wrap='nowrap'>
            {matchCountText}
          </Text>
        )}
      </MatchCountSlot>
      <Button
        type='button'
        variant='ghost'
        size='icon-sm'
        disabled={hits.length === 0}
        onClick={goToPrevious}
        title={t`Previous match (Shift+Enter)`}
        aria-label={t`Previous match`}>
        <ChevronUp />
      </Button>
      <Button
        type='button'
        variant='ghost'
        size='icon-sm'
        disabled={hits.length === 0}
        onClick={goToNext}
        title={t`Next match (Enter)`}
        aria-label={t`Next match`}>
        <ChevronDown />
      </Button>
      <Button
        type='button'
        variant='ghost'
        size='icon-sm'
        onClick={close}
        title={t`Close (Escape)`}
        aria-label={t`Close find`}>
        <X />
      </Button>
    </SearchBar>
  )
}
